import {
  useEffect,
  useCallback,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  RefObject,
  PointerEvent as ReactPointerEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { ZoomModalImage } from "./ZoomModalImage";
import { ZoomModalVideo } from "./ZoomModalVideo";
import { Thumbnails } from "./Thumbnails";
import { SuperZoomModal } from "./SuperZoomModal";
import { GalleryObject, ImageClickPosition } from "../utils/utils.ts";
import { dispatchGalleryMonetateQEvent } from "@/components/Galleries/components/Analytics/dispatchGalleryMonetateQEvent";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";
import styles from "./Carousel.module.scss";

type CarouselProps = {
  mediaItems: GalleryObject[];
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  videoAutoplayIndex: number | null;
};

type CarouselItemProps = {
  mediaItem: GalleryObject;
  totalMediaItems: number;
  index: number;
  intersectionObserver: IntersectionObserver | null;
  scrollToIndex: (index: number) => void;
  isActiveVideo: boolean;
  onVideoPlayStateChange: (index: number, isPlaying: boolean) => void;
  videoAutoplayIndex: number | null;
  handleSuperZoomOpen: (
    event:
      | ReactPointerEvent<HTMLButtonElement>
      | ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
    imageRef: RefObject<HTMLButtonElement | null>,
  ) => void;
};

const CarouselItem = ({
  mediaItem,
  totalMediaItems,
  index,
  intersectionObserver,
  scrollToIndex,
  isActiveVideo,
  onVideoPlayStateChange,
  videoAutoplayIndex,
  handleSuperZoomOpen,
}: CarouselItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (itemRef.current && intersectionObserver) {
      intersectionObserver.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current && intersectionObserver) {
        intersectionObserver.unobserve(itemRef.current);
      }
    };
  }, [intersectionObserver]);

  if (!mediaItem) {
    return null;
  }

  return (
    <div
      key={mediaItem.imageUrl}
      data-gallery-item-index={index}
      className={styles.galleryItem}
      ref={itemRef}
    >
      {mediaItem.type === "video" ? (
        <ZoomModalVideo
          video={mediaItem}
          index={index}
          total={totalMediaItems}
          scrollToIndex={scrollToIndex}
          isActive={isActiveVideo}
          onPlayStateChange={onVideoPlayStateChange}
          videoAutoplayIndex={videoAutoplayIndex}
        />
      ) : (
        <ZoomModalImage
          mediaItem={mediaItem}
          index={index}
          total={totalMediaItems}
          handleSuperZoomOpen={handleSuperZoomOpen}
        />
      )}
    </div>
  );
};

export const Carousel = ({
  mediaItems,
  selectedIndex,
  setSelectedIndex,
  videoAutoplayIndex,
}: CarouselProps) => {
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const thumbsContainerRef = useRef<HTMLDivElement>(null);
  const clickTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [fullscreenAnimationEnded, setfullscreenAnimationEnded] =
    useState(false);
  const [intersectionObserver, setIntersectionObserver] =
    useState<IntersectionObserver | null>(null);
  const [autoScroll, setAutoScroll] = useState(false);
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const [isSuperZoomOpen, setIsSuperZoomOpen] = useState(false);
  const [superZoomSelectedIndex, setSuperZoomSelectedIndex] = useState(0);
  const [imageClickPosition, setImageClickPoisition] =
    useState<ImageClickPosition>({ x: 0, y: 0 });
  const { flags } = useStandardProps();
  const hasDeferredMonetateQEvents = !!flags.hasDeferredMonetateQEvents.value;
  const premiumSuperZoomEnabled = flags.premiumSuperZoom.value === true;
  const modalMode =
    mediaItems[0]?.type === "video" ? "videoModal" : "imageModal";

  const pointerCache: ReactPointerEvent[] = [];
  const doubleClickTimeout = 200;

  const isLandscapeLayout = () =>
    window.matchMedia("(orientation: landscape)").matches;

  const handleVideoPlayStateChange = (index: number, playing: boolean) => {
    setActiveVideoIndex((current) => {
      if (playing) return index;
      return current === index ? null : current;
    });
  };

  const scrollImagesToIndex = async (
    index: number,
    behaviour: ScrollBehavior = "smooth",
  ) => {
    if (index < 0 || index >= mediaItems.length || !mainContainerRef.current) {
      return;
    }
    const target = mainContainerRef.current.children[index];
    if (!(target instanceof HTMLElement)) return;

    let thumbnailHeight = 0;
    if (thumbsContainerRef.current && !isLandscapeLayout()) {
      thumbnailHeight =
        thumbsContainerRef.current.getBoundingClientRect().height;
    }
    const difference =
      (window.innerHeight - thumbnailHeight - target.clientHeight) / 2;
    const offsetTop =
      difference < 0
        ? target.offsetTop + Math.abs(difference)
        : target.offsetTop - Math.abs(difference);

    setAutoScroll(true);
    const result = await mainContainerRef.current.scrollTo({
      left: target.offsetLeft,
      top: offsetTop,
      behavior: behaviour,
    });
    setAutoScroll(false);
  };

  const scrollThumnailsToIndex = async (index: number) => {
    if (
      index < 0 ||
      index >= mediaItems.length ||
      !thumbsContainerRef.current
    ) {
      return;
    }
    const target = thumbsContainerRef.current.children[index];
    if (!(target instanceof HTMLElement)) return;

    const offsetTop = isLandscapeLayout()
      ? target.offsetTop -
        thumbsContainerRef.current.getBoundingClientRect().height / 2 +
        target.getBoundingClientRect().height / 2
      : target.offsetTop;
    const offsetLeft = !isLandscapeLayout()
      ? target.offsetLeft -
        thumbsContainerRef.current.getBoundingClientRect().width / 2 +
        target.getBoundingClientRect().width / 2
      : target.offsetLeft;
    thumbsContainerRef.current.scrollTo(offsetLeft, offsetTop);
  };

  const handleFullscreenAnimationEnd = () => {
    setfullscreenAnimationEnded(true);
    scrollImagesToIndex(selectedIndex, "instant");
    scrollThumnailsToIndex(selectedIndex);
  };

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      setActiveVideoIndex((current) => {
        if (current === null) return current;

        const activeEntry = entries.find(
          ({ target }) =>
            target === mainContainerRef.current?.children[current],
        );

        return activeEntry &&
          (!activeEntry.isIntersecting || activeEntry.intersectionRatio < 0.3)
          ? null
          : current;
      });

      const isRelevant = (intersectionEntry: IntersectionObserverEntry) => {
        if (
          (isLandscapeLayout() &&
            intersectionEntry.intersectionRatio >= 0.3 &&
            intersectionEntry.intersectionRatio < 0.6) ||
          (!isLandscapeLayout() &&
            !(
              intersectionEntry.intersectionRatio >= 0.3 &&
              intersectionEntry.intersectionRatio < 0.6
            ))
        ) {
          return true;
        }
        return false;
      };

      const intersectingEntries = entries
        .filter(({ isIntersecting }) => isIntersecting)
        .filter(isRelevant);

      if (
        mainContainerRef.current &&
        intersectingEntries &&
        intersectingEntries.length > 0
      ) {
        const maxIntersecting = intersectingEntries.reduce((prev, current) => {
          return prev && prev.intersectionRatio > current.intersectionRatio
            ? prev
            : current;
        });

        const intersectionIndex = [
          ...mainContainerRef.current.children,
        ].indexOf(maxIntersecting.target);

        dispatchGalleryMonetateQEvent(
          "ModalImageScroll",
          modalMode,
          hasDeferredMonetateQEvents,
        );

        if (!autoScroll) {
          setSelectedIndex(intersectionIndex);
          setActiveVideoIndex((current) =>
            current === intersectionIndex ? current : null,
          );
        }
      }
    },
    [autoScroll, hasDeferredMonetateQEvents, modalMode, setSelectedIndex],
  );

  const handleScrollIntoView = () => {
    if (mainContainerRef.current) {
      mainContainerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const calculateClickPosition = (
    event:
      | ReactPointerEvent<HTMLButtonElement>
      | ReactKeyboardEvent<HTMLButtonElement>,
    imageRef: RefObject<HTMLButtonElement | null>,
  ): ImageClickPosition => {
    if (!imageRef.current) {
      return { x: 0, y: 0 };
    }
    if (!("clientX" in event)) {
      return { x: 0, y: 0 };
    }

    const {
      nativeEvent: { clientX, clientY },
    } = event;

    const imageBoundingClient = imageRef.current.getBoundingClientRect();
    const relativeMousePositionX = clientX - imageBoundingClient.left;
    const relativeMousePositionY = clientY - imageBoundingClient.top;
    return { x: relativeMousePositionX, y: relativeMousePositionY };
  };

  const openSuperZoom = (
    event:
      | ReactPointerEvent<HTMLButtonElement>
      | ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
    imageRef: RefObject<HTMLButtonElement | null>,
  ) => {
    if (premiumSuperZoomEnabled) {
      setSuperZoomSelectedIndex(index);
      setImageClickPoisition(calculateClickPosition(event, imageRef));
      setIsSuperZoomOpen(true);
    }
  };

  const removeEventFromCache = (event: ReactPointerEvent) => {
    // Remove this event from the target's cache
    const index = pointerCache.findIndex(
      (cachedEvent) => cachedEvent.pointerId === event.pointerId,
    );
    pointerCache.splice(index, 1);
  };

  const handleSuperZoomOpen = (
    event:
      | ReactPointerEvent<HTMLButtonElement>
      | ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
    imageRef: RefObject<HTMLButtonElement | null>,
  ) => {
    dispatchGalleryMonetateQEvent("ClickOnModalImage", "imageModal");

    if ("code" in event && event.nativeEvent.type === "keyup") {
      // Keyboard Space or Enter opens the super zoom
      if (event.code === "Space" || event.code === "Enter") {
        openSuperZoom(event, index, imageRef);
      }
    } else if ("pointerType" in event) {
      if (
        event.nativeEvent.pointerType === "mouse" &&
        event.nativeEvent.type === "pointerup"
      ) {
        // Single mouse click opens the super zoom
        openSuperZoom(event, index, imageRef);
      } else {
        if (event.nativeEvent.type === "pointerdown") {
          pointerCache.push(event);
        } else {
          // Double tap of touch device opens the super zoom
          if (pointerCache.length > 1) {
            // Multiple simultaneous pointer events, can't be a double tap
            removeEventFromCache(event);
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = undefined;
            }
          } else {
            removeEventFromCache(event);
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = undefined;
              if (
                event.nativeEvent.type === "pointerup" &&
                pointerCache.length === 0
              ) {
                openSuperZoom(event, index, imageRef);
              }
            } else {
              clickTimerRef.current = setTimeout(() => {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = undefined;
              }, doubleClickTimeout);
            }
          }
        }
      }
    }
  };

  const closeSuperZoom = () => {
    setIsSuperZoomOpen(false);
  };

  const handleSuperZoomClose = (
    event:
      ReactPointerEvent<HTMLDivElement> | ReactKeyboardEvent<HTMLDivElement>,
  ) => {
    if ("code" in event && event.nativeEvent.type === "keyup") {
      // Keyboard Space or Enter closes the super zoom
      if (event.code === "Space" || event.code === "Enter") {
        closeSuperZoom();
      }
    } else if ("pointerType" in event) {
      if (
        event.nativeEvent.pointerType === "mouse" &&
        event.nativeEvent.type === "pointerup"
      ) {
        // Single mouse click closes the super zoom
        closeSuperZoom();
      } else {
        if (event.nativeEvent.type === "pointerdown") {
          pointerCache.push(event);
        } else {
          // Double tap of touch device close the super zoom
          if (pointerCache.length > 1) {
            // Multiple simultaneous pointer events, can't be a double tap
            removeEventFromCache(event);
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = undefined;
            }
          } else {
            removeEventFromCache(event);
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = undefined;
              if (
                event.nativeEvent.type === "pointerup" &&
                pointerCache.length === 0
              ) {
                closeSuperZoom();
              }
            } else {
              clickTimerRef.current = setTimeout(() => {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = undefined;
              }, doubleClickTimeout);
            }
          }
        }
      }
    }
  };

  useEffect(() => {
    if (!fullscreenAnimationEnded) return;

    const observer = new IntersectionObserver(
      (entries) => handleIntersection(entries),
      {
        root: mainContainerRef.current,
        threshold: [0.3, 0.6, 1],
      },
    );

    setIntersectionObserver(observer);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersection, fullscreenAnimationEnded]);

  useEffect(() => {
    const modalElement = mainContainerRef.current?.closest(
      '[data-testid="modal"]',
    );

    modalElement?.addEventListener(
      "animationstart",
      handleFullscreenAnimationEnd,
      {
        once: true,
      },
    );

    document.addEventListener(
      "carousel-gallery-scroll-into-view",
      handleScrollIntoView,
    );

    return () => {
      modalElement?.removeEventListener(
        "animationstart",
        handleFullscreenAnimationEnd,
      );

      document.removeEventListener(
        "carousel-gallery-scroll-into-view",
        handleScrollIntoView,
      );
    };
  }, []);

  return (
    <div className={styles.wrapper} data-testid={"gallery:zoom:modal:carousel"}>
      <div className={styles.main}>
        <div className={styles.gallery} ref={mainContainerRef}>
          {mediaItems.map((mediaItem, ind) => (
            <CarouselItem
              mediaItem={mediaItem}
              totalMediaItems={mediaItems.length}
              intersectionObserver={intersectionObserver}
              index={ind}
              scrollToIndex={scrollImagesToIndex}
              isActiveVideo={activeVideoIndex === ind}
              onVideoPlayStateChange={handleVideoPlayStateChange}
              key={`${mediaItem.imageUrl}-${ind}`}
              videoAutoplayIndex={videoAutoplayIndex}
              handleSuperZoomOpen={handleSuperZoomOpen}
            />
          ))}
        </div>
        <Thumbnails
          media={mediaItems}
          selectedIndex={selectedIndex}
          scrollImagesToIndex={scrollImagesToIndex}
          scrollThumbnailsToIndex={scrollThumnailsToIndex}
          autoScroll={autoScroll}
          ref={thumbsContainerRef}
        />
      </div>
      {premiumSuperZoomEnabled && (
        <SuperZoomModal
          isOpen={isSuperZoomOpen}
          mediaItems={mediaItems}
          selectedIndex={superZoomSelectedIndex}
          imageClickPosition={imageClickPosition}
          onCloseButton={closeSuperZoom}
          onCloseClick={handleSuperZoomClose}
        />
      )}
      <div
        className={styles.visuallyHidden}
        aria-live="polite"
        aria-atomic="true"
      >
        {`Viewing image ${selectedIndex + 1} of ${mediaItems.length}`}
      </div>
    </div>
  );
};
